import React, { useRef, useEffect, useState } from 'react';
import OpenSeadragon from 'openseadragon';

interface ImageNavigatorProps {
  viewer: OpenSeadragon.Viewer | null;
  className?: string;
}

export function ImageNavigator({ viewer, className = '' }: ImageNavigatorProps) {
  const navigatorRef = useRef<HTMLDivElement>(null);
  const [isVisible, setIsVisible] = useState(true);
  const [navigatorViewer, setNavigatorViewer] = useState<OpenSeadragon.Viewer | null>(null);

  useEffect(() => {
    if (!viewer || !navigatorRef.current) return;

    try {
      // Wait for main viewer to be ready
      if (!viewer.isOpen()) {
        const openHandler = () => {
          initializeNavigator();
          viewer.removeHandler('open', openHandler);
        };
        viewer.addHandler('open', openHandler);
      } else {
        initializeNavigator();
      }
    } catch (err) {
      console.error('Error setting up navigator:', err);
    }

    function initializeNavigator() {
      if (!viewer || !navigatorRef.current) return;

      try {
        // Clean up existing navigator
        if (navigatorViewer) {
          navigatorViewer.destroy();
          setNavigatorViewer(null);
        }

        // Create navigator viewer
        const nav = OpenSeadragon({
          element: navigatorRef.current,
          prefixUrl: 'https://cdn.jsdelivr.net/npm/openseadragon@4.1.0/build/openseadragon/images/',
          
          // Use the same tile sources as main viewer
          tileSources: viewer.tileSources,
          
          // Navigator-specific settings
          showNavigationControl: false,
          showZoomControl: false,
          showHomeControl: false,
          showFullPageControl: false,
          showRotationControl: false,
          showSequenceControl: false,
          mouseNavEnabled: true,
          
          // Make it smaller and simpler
          animationTime: 0.2,
          blendTime: 0.05,
          
          // Performance settings for mini-map
          immediateRender: true,
          preserveImageSizeOnResize: true,
          preserveViewport: false,
          
          // Styling
          borderColor: '#00ff00',
          displayRegionColor: '#00ff00',
          
          // Size and constraints
          minZoomLevel: 0.01,
          maxZoomLevel: 1.0,
          visibilityRatio: 1.0,
          
          // Touch settings for mobile
          gestureSettingsPan: {
            scrollToZoom: false,
            clickToZoom: false,
            dblClickToZoom: false,
            pinchToZoom: false,
            flickEnabled: false
          }
        });

        setNavigatorViewer(nav);

        // Sync navigator with main viewer
        const syncNavigator = () => {
          if (nav && viewer) {
            const mainViewport = viewer.viewport;
            const navViewport = nav.viewport;
            
            // Sync the viewport rectangle
            const bounds = mainViewport.getBounds();
            navViewport.fitBounds(bounds, true);
          }
        };

        // Listen to main viewer changes
        viewer.addHandler('zoom', syncNavigator);
        viewer.addHandler('pan', syncNavigator);
        viewer.addHandler('resize', syncNavigator);

        // Handle navigator clicks to navigate main viewer
        nav.addHandler('canvas-click', (event: any) => {
          if (viewer && event.position) {
            const viewportPoint = nav.viewport.pointFromPixel(event.position);
            const imagePoint = nav.viewport.viewportToImageCoordinates(viewportPoint);
            const mainImagePoint = viewer.viewport.imageToViewportCoordinates(imagePoint);
            viewer.viewport.panTo(mainImagePoint, true);
          }
        });

        // Initial sync
        nav.addHandler('open', syncNavigator);

      } catch (err) {
        console.error('Failed to initialize navigator:', err);
      }
    }

    // Cleanup
    return () => {
      if (navigatorViewer) {
        try {
          navigatorViewer.destroy();
        } catch (err) {
          console.error('Error destroying navigator:', err);
        }
      }
    };
  }, [viewer, navigatorViewer]);

  if (!viewer) return null;

  return (
    <div className={`absolute bottom-4 right-4 z-20 ${className}`}>
      <div className={`
        bg-black bg-opacity-80 backdrop-blur-sm rounded-lg shadow-lg border border-white/20 p-2
        transition-all duration-300
        ${isVisible ? 'opacity-100 scale-100' : 'opacity-0 scale-95 pointer-events-none'}
      `}>
        {/* Navigator header */}
        <div className="flex items-center justify-between mb-2">
          <span className="text-xs text-gray-300 font-medium">Πλοήγηση</span>
          <button
            onClick={() => setIsVisible(!isVisible)}
            className="text-gray-400 hover:text-white transition-colors text-xs p-1"
            title={isVisible ? 'Απόκρυψη navigator' : 'Εμφάνιση navigator'}
          >
            {isVisible ? '−' : '+'}
          </button>
        </div>

        {/* Navigator viewport */}
        <div className="relative">
          <div
            ref={navigatorRef}
            className="w-32 h-32 sm:w-40 sm:h-40 bg-gray-900 border border-gray-600 rounded cursor-pointer"
            style={{
              minWidth: '128px',
              minHeight: '128px'
            }}
          />
          
          {/* Loading indicator for navigator */}
          {!navigatorViewer && (
            <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-50 rounded">
              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
            </div>
          )}
        </div>

        {/* Navigator instructions */}
        <div className="mt-2 text-xs text-gray-400 text-center">
          Κλικ για πλοήγηση
        </div>
      </div>

      {/* Toggle button for mobile when navigator is hidden */}
      {!isVisible && (
        <button
          onClick={() => setIsVisible(true)}
          className="
            w-12 h-12 bg-black bg-opacity-80 backdrop-blur-sm rounded-full
            border border-white/20 flex items-center justify-center
            text-white hover:bg-opacity-100 transition-all
            touch-manipulation
          "
          title="Εμφάνιση navigator"
        >
          <span className="text-lg">🗺️</span>
        </button>
      )}
    </div>
  );
}